import { supabase } from '@/lib/supabase';

// ── Accès aux produits digitaux ─────────────────────────────────────
// Un produit digital (livre, PDF, vidéo…) donne accès à son fichier via
// une ligne d'accès. L'accès est créé soit MANUELLEMENT par le marchand
// (il choisit un client Zifek), soit AUTOMATIQUEMENT quand le marchand
// valide le paiement d'une commande contenant ce produit.
//
// Chaque attribution / retrait est également consigné dans un JOURNAL
// D'AUDIT (digital_access_audit) afin de garder l'historique des accès
// retirés, consultable par produit et par client.

const ACCESS_TABLE = 'digital_product_access';
const AUDIT_TABLE = 'digital_access_audit';

export interface DigitalAccessEntry {
  id: number;
  productId: number;
  productName: string | null;
  /** Type réel du produit lié : 'digital' ou 'physical'. */
  productType: string | null;
  userId: number;
  merchantId: number | null;
  orderId: number | null;
  source: string;
  status: string;
  grantedAt: string;
}

export interface DigitalAccessAuditEntry {
  id: number;
  merchantId: number | null;
  productId: number;
  productName: string | null;
  userId: number;
  orderId: number | null;
  /** 'grant' (accordé) ou 'revoke' (retiré). */
  action: string;
  source: string;
  createdAt: string;
}

export interface ZifekClient {
  id: number;
  name: string;
  userName: string;
  email: string;
  image: string | null;
}

function mapAccess(row: Record<string, unknown>): DigitalAccessEntry {
  return {
    id: Number(row.id),
    productId: Number(row.product_id),
    productName: (row.product_name as string) || null,
    productType: null,
    userId: Number(row.user_id),
    merchantId: row.merchant_id != null ? Number(row.merchant_id) : null,
    orderId: row.order_id != null ? Number(row.order_id) : null,
    source: (row.source as string) || 'manual',
    status: (row.status as string) || 'active',
    grantedAt: (row.granted_at as string) || '',
  };
}

function mapAudit(row: Record<string, unknown>): DigitalAccessAuditEntry {
  return {
    id: Number(row.id),
    merchantId: row.merchant_id != null ? Number(row.merchant_id) : null,
    productId: Number(row.product_id),
    productName: (row.product_name as string) || null,
    userId: Number(row.user_id),
    orderId: row.order_id != null ? Number(row.order_id) : null,
    action: (row.action as string) || 'grant',
    source: (row.source as string) || 'manual',
    createdAt: (row.created_at as string) || '',
  };
}

export function mapClient(row: Record<string, unknown>): ZifekClient {
  return {
    id: Number(row.id),
    name: (row.name as string) || (row.user_name as string) || `Client #${row.id}`,
    userName: (row.user_name as string) || '',
    email: (row.email as string) || '',
    image: (row.image as string) || null,
  };
}

/** Récupère le type réel (digital / physique) des produits concernés. */
async function productTypeMap(ids: number[]): Promise<Record<number, string>> {
  const unique = Array.from(new Set(ids.filter((n) => Number.isFinite(n))));
  if (unique.length === 0) return {};
  const { data, error } = await supabase
    .from('product_items')
    .select('id, product_type')
    .in('id', unique);
  if (error) throw error;
  const map: Record<number, string> = {};
  (data || []).forEach((row: { id: number; product_type: string | null }) => {
    map[Number(row.id)] = row.product_type || 'physical';
  });
  return map;
}

/** Enrichit une liste d'accès avec le type réel de chaque produit. */
async function withProductTypes(entries: DigitalAccessEntry[]): Promise<DigitalAccessEntry[]> {
  if (entries.length === 0) return entries;
  const map = await productTypeMap(entries.map((e) => e.productId));
  return entries.map((e) => ({ ...e, productType: map[e.productId] ?? null }));
}

/** Consigne une action dans le journal d'audit (ne bloque jamais l'action principale). */
async function logAudit(entry: {
  merchantId: number | null;
  productId: number;
  productName: string | null;
  userId: number;
  orderId: number | null;
  action: 'grant' | 'revoke';
  source: string;
}): Promise<void> {
  try {
    const { error } = await supabase.from(AUDIT_TABLE).insert({
      merchant_id: entry.merchantId,
      product_id: entry.productId,
      product_name: entry.productName,
      user_id: entry.userId,
      order_id: entry.orderId,
      action: entry.action,
      source: entry.source,
      created_at: new Date().toISOString(),
    });
    if (error) console.error('[digitalAccess] audit log failed:', error.message);
  } catch (err) {
    console.error('[digitalAccess] audit log failed:', err);
  }
}

/** Accès actifs d'un produit (pour l'écran marchand). */
export async function listAccessForProduct(productId: number): Promise<DigitalAccessEntry[]> {
  const { data, error } = await supabase
    .from(ACCESS_TABLE)
    .select('*')
    .eq('product_id', productId)
    .eq('status', 'active')
    .order('granted_at', { ascending: false });
  if (error) throw error;
  return withProductTypes((data || []).map((row) => mapAccess(row as Record<string, unknown>)));
}

/**
 * TOUS les accès actifs d'un marchand, tous produits confondus.
 * Utilisé par la page « Accès digitaux » pour gérer/retirer les accès
 * au même endroit, sans passer par chaque fiche produit.
 */
export async function listAllAccessForMerchant(merchantId: number): Promise<DigitalAccessEntry[]> {
  const { data, error } = await supabase
    .from(ACCESS_TABLE)
    .select('*')
    .eq('merchant_id', merchantId)
    .eq('status', 'active')
    .order('granted_at', { ascending: false });
  if (error) throw error;
  return withProductTypes((data || []).map((row) => mapAccess(row as Record<string, unknown>)));
}

/** Accès actifs d'un client donné (pour son espace « Mes téléchargements »). */
export async function listAccessForUser(userId: number): Promise<DigitalAccessEntry[]> {
  const { data, error } = await supabase
    .from(ACCESS_TABLE)
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('granted_at', { ascending: false });
  if (error) throw error;
  return withProductTypes((data || []).map((row) => mapAccess(row as Record<string, unknown>)));
}

/** Accordé (ou réactivé) : accorde l'accès d'un client à un produit digital + journalise. */
export async function grantDigitalAccess(params: {
  productId: number;
  productName?: string | null;
  userId: number;
  merchantId?: number | null;
  orderId?: number | null;
  source?: 'manual' | 'payment';
}): Promise<void> {
  const { error } = await supabase.from(ACCESS_TABLE).upsert(
    {
      product_id: params.productId,
      product_name: params.productName ?? null,
      user_id: params.userId,
      merchant_id: params.merchantId ?? null,
      order_id: params.orderId ?? null,
      source: params.source || 'manual',
      status: 'active',
      granted_at: new Date().toISOString(),
    },
    { onConflict: 'product_id,user_id' },
  );
  if (error) throw error;
  await logAudit({
    merchantId: params.merchantId ?? null,
    productId: params.productId,
    productName: params.productName ?? null,
    userId: params.userId,
    orderId: params.orderId ?? null,
    action: 'grant',
    source: params.source || 'manual',
  });
}

/** Retire l'accès et consigne le retrait dans le journal d'audit. */
export async function revokeDigitalAccess(id: number): Promise<void> {
  // On lit la ligne AVANT suppression pour pouvoir tracer le produit/le client.
  const { data: row } = await supabase.from(ACCESS_TABLE).select('*').eq('id', id).maybeSingle();
  const { error } = await supabase.from(ACCESS_TABLE).delete().eq('id', id);
  if (error) throw error;
  if (row) {
    const r = row as Record<string, unknown>;
    await logAudit({
      merchantId: r.merchant_id != null ? Number(r.merchant_id) : null,
      productId: Number(r.product_id),
      productName: (r.product_name as string) || null,
      userId: Number(r.user_id),
      orderId: r.order_id != null ? Number(r.order_id) : null,
      action: 'revoke',
      source: (r.source as string) || 'manual',
    });
  }
}

/** Historique d'audit d'un marchand (accordés + retirés), le plus récent d'abord. */
export async function listAccessAudit(merchantId: number, limit = 400): Promise<DigitalAccessAuditEntry[]> {
  const { data, error } = await supabase
    .from(AUDIT_TABLE)
    .select('*')
    .eq('merchant_id', merchantId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((row) => mapAudit(row as Record<string, unknown>));
}

/** Recherche de clients Zifek existants (nom, pseudo ou email). */
export async function searchZifekClients(query: string, limit = 8): Promise<ZifekClient[]> {
  const q = query.trim();
  if (!q) return [];
  const { data, error } = await supabase
    .from('users')
    .select('id, name, user_name, email, image')
    .or(`name.ilike.%${q}%,user_name.ilike.%${q}%,email.ilike.%${q}%`)
    .limit(limit);
  if (error) throw error;
  return (data || []).map((row) => mapClient(row as Record<string, unknown>));
}

/** Récupère les infos de plusieurs clients (affichage des détenteurs d'accès). */
export async function listZifekClientsByIds(ids: number[]): Promise<ZifekClient[]> {
  const unique = Array.from(new Set(ids.filter((n) => Number.isFinite(n))));
  if (unique.length === 0) return [];
  const { data, error } = await supabase
    .from('users')
    .select('id, name, user_name, email, image')
    .in('id', unique);
  if (error) throw error;
  return (data || []).map((row) => mapClient(row as Record<string, unknown>));
}